import { SchemaField, getEffectiveDisplayType, flattenSchema } from '@doneverse/data/utils'
import { buildBriefFormSchema, type BriefValues } from '@doneverse/data/utils'
import { isFieldVisible, type PlaybookSchema } from '@doneverse/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { ImageSelectField } from './ImageSelectField'
import { MultiListField } from './MultiListField'
import { RankField } from './RankField'
import { Checkbox } from '@/components/ui/checkbox'
import { Combobox } from '@/components/ui/combobox'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { readFormDraft, writeFormDraft } from '@/lib/formDraft'

export interface DynamicBriefFormHandle {
  submit: () => Promise<boolean>
  setValue: (field: string, value: string | string[]) => void
  getValues: () => BriefValues
}

type BriefSchemaInput = SchemaField[] | PlaybookSchema

function normalizeSchema(schema: BriefSchemaInput): SchemaField[] {
  if (!schema.length) return schema as SchemaField[]

  const first = schema[0] as unknown
  if (
    typeof first === 'object' &&
    first !== null &&
    'questions' in first &&
    Array.isArray((first as { questions?: unknown }).questions)
  ) {
    return flattenSchema(schema as PlaybookSchema)
  }

  return schema as SchemaField[]
}

interface DynamicBriefFormProps {
  schema: BriefSchemaInput
  defaultValues?: BriefValues
  onSubmit: (values: BriefValues) => void
  onValuesChange?: (values: BriefValues) => void
  disabled?: boolean
  formId?: string
  draftStorageKey?: string
  draftDebounceMs?: number
  /** 'default' uses standard shadcn styling; 'embedded' uses gray bg / borderless inputs */
  variant?: 'default' | 'embedded'
  /** Show validation errors but allow submission anyway (e.g. for admin edits) */
  softValidation?: boolean
  /** Trigger validation on mount and focus the first invalid field. */
  validateOnMount?: boolean
}

export const DynamicBriefForm = forwardRef<DynamicBriefFormHandle, DynamicBriefFormProps>(
  function DynamicBriefForm(
    {
      schema,
      defaultValues,
      onSubmit,
      onValuesChange,
      disabled,
      formId,
      draftStorageKey,
      draftDebounceMs = 300,
      variant = 'embedded',
      softValidation = false,
      validateOnMount = false,
    },
    ref,
  ) {
    const normalizedSchema = useMemo(() => normalizeSchema(schema), [schema])
    const zodSchema = useMemo(() => buildBriefFormSchema(normalizedSchema), [normalizedSchema])
    const draftSaveTimeoutRef = useRef<number | null>(null)

    const coerceValuesToSchema = useCallback(
      (values: BriefValues): BriefValues => {
        const coerced: BriefValues = { ...values }
        for (const field of normalizedSchema) {
          if (field.type === 'section') continue
          const isArrayField =
            field.field_type === 'rank' ||
            field.field_type === 'multi_select' ||
            field.displayAs === 'multi_list'
          if (!isArrayField) continue
          const fieldId = String(field.id)
          const value = coerced[fieldId]
          if (typeof value === 'string') {
            coerced[fieldId] = value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          }
        }
        return coerced
      },
      [normalizedSchema],
    )

    // Merge async-loaded defaults with any persisted draft. Driven via RHF's
    // `values` prop so refetches don't blow away dirty values or errors.
    const mergedValues = useMemo(() => {
      const persistedDraft = draftStorageKey ? readFormDraft<BriefValues>(draftStorageKey) : null
      const merged = { ...(defaultValues ?? {}), ...(persistedDraft ?? {}) }
      return Object.keys(merged).length > 0 ? coerceValuesToSchema(merged) : undefined
    }, [defaultValues, draftStorageKey, coerceValuesToSchema])

    const form = useForm<BriefValues>({
      resolver: zodResolver(zodSchema),
      defaultValues: mergedValues,
      values: mergedValues,
      resetOptions: {
        keepDirtyValues: true,
        keepErrors: true,
      },
    })
    const { control, getValues, handleSubmit, setValue, trigger, watch } = form

    // Notify parent of value changes
    useEffect(() => {
      if (!onValuesChange) return
      const subscription = watch((values) => {
        onValuesChange(values as BriefValues)
      })
      return () => subscription.unsubscribe()
    }, [onValuesChange, watch])

    // Persist draft values to localStorage (debounced) when configured.
    useEffect(() => {
      if (!draftStorageKey) return

      const subscription = watch((values) => {
        if (draftSaveTimeoutRef.current !== null) {
          window.clearTimeout(draftSaveTimeoutRef.current)
        }

        draftSaveTimeoutRef.current = window.setTimeout(() => {
          writeFormDraft(draftStorageKey, values as BriefValues)
        }, draftDebounceMs)
      })

      return () => {
        subscription.unsubscribe()

        if (draftSaveTimeoutRef.current !== null) {
          window.clearTimeout(draftSaveTimeoutRef.current)
          draftSaveTimeoutRef.current = null
        }
      }
    }, [watch, draftDebounceMs, draftStorageKey])

    // Validate on mount so existing errors are visible immediately and
    // the first invalid field gets focused (RHF handles the focus).
    const didValidateOnMountRef = useRef(false)
    useEffect(() => {
      if (!validateOnMount || didValidateOnMountRef.current) return
      didValidateOnMountRef.current = true
      void trigger(undefined, { shouldFocus: true })
    }, [validateOnMount, trigger])

    useImperativeHandle(ref, () => ({
      submit: () =>
        new Promise<boolean>((resolve) => {
          handleSubmit(
            (data) => {
              onSubmit(data)
              resolve(true)
            },
            () => {
              if (softValidation) {
                onSubmit(getValues())
                resolve(true)
              } else {
                resolve(false)
              }
            },
          )()
        }),
      setValue: (field, value) => {
        setValue(field, value, { shouldDirty: true })
      },
      getValues: () => getValues(),
    }))

    const watchedValues = watch()

    const shouldShowField = (field: SchemaField): boolean => isFieldVisible(field.showWhen, watchedValues)

    const isDefault = variant === 'default'
    const labelClasses = isDefault
      ? 'font-semibold text-xs text-black'
      : 'text-sm font-normal text-foreground'
    const spacing = isDefault ? 'space-y-6' : 'space-y-5'

    return (
      <Form {...form}>
        <form
          id={formId}
          noValidate
          onSubmit={handleSubmit(onSubmit, softValidation ? () => onSubmit(getValues()) : undefined)}
          className={spacing}
        >
          {normalizedSchema.map((field) => {
            if (!shouldShowField(field)) return null

            // Section header
            if (field.type === 'section') {
              return (
                <div
                  key={`section-${field.id}`}
                  className={isDefault ? 'pt-8 first:pt-0' : 'pt-6 first:pt-0'}
                >
                  <h4
                    className={
                      isDefault ? 'font-bold text-lg text-foreground' : 'font-bold text-base text-foreground'
                    }
                  >
                    {field.title}
                  </h4>
                  {field.description && (
                    <p className="text-sm text-muted-foreground mt-1">{field.description}</p>
                  )}
                </div>
              )
            }

            const fieldId = String(field.id)

            const displayType = getEffectiveDisplayType(field)

            return (
              <FormField
                key={fieldId}
                control={control}
                name={fieldId}
                render={({ field: formField }) => (
                  <FormItem className={isDefault ? '' : 'space-y-1.5'} data-brief-field={fieldId}>
                    {displayType !== 'checkbox' && (
                      <FormLabel className={labelClasses}>
                        {field.question}
                        {!isDefault && field.required && <span className="text-destructive ml-1">*</span>}
                      </FormLabel>
                    )}
                    {field.description && displayType !== 'checkbox' && (
                      <p className="text-xs text-muted-foreground">{field.description}</p>
                    )}

                    {/* Short Text */}
                    {displayType === 'short_text' && (
                      <FormControl>
                        <Input
                          {...formField}
                          value={(formField.value as string) ?? ''}
                          disabled={disabled}
                          placeholder="Type your answer..."
                        />
                      </FormControl>
                    )}

                    {/* Multi List (multiple short text inputs) */}
                    {displayType === 'multi_list' && (
                      <FormControl>
                        <MultiListField
                          ref={formField.ref}
                          value={Array.isArray(formField.value) ? (formField.value as string[]) : ['']}
                          onChange={formField.onChange}
                          disabled={disabled}
                          fieldId={fieldId}
                        />
                      </FormControl>
                    )}

                    {/* Long Text */}
                    {displayType === 'long_text' && (
                      <FormControl>
                        <Textarea
                          {...formField}
                          value={(formField.value as string) ?? ''}
                          disabled={disabled}
                          placeholder="Type your answer..."
                          className="min-h-[80px]"
                        />
                      </FormControl>
                    )}

                    {/* Long Text XL */}
                    {displayType === 'long_text_xl' && (
                      <FormControl>
                        <Textarea
                          {...formField}
                          value={(formField.value as string) ?? ''}
                          disabled={disabled}
                          placeholder="Type your answer..."
                          className="min-h-[150px]"
                        />
                      </FormControl>
                    )}

                    {/* Single Select */}
                    {displayType === 'single_select' && field.options && (
                      <Select
                        value={(formField.value as string) ?? ''}
                        onValueChange={formField.onChange}
                        disabled={disabled}
                      >
                        <FormControl>
                          <SelectTrigger ref={formField.ref} aria-label={field.question}>
                            <SelectValue placeholder="Select an option..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-background">
                          {field.options.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* Searchable Select (long option lists, e.g. timezones) */}
                    {displayType === 'searchable_select' && field.options && (
                      <Combobox
                        ref={formField.ref}
                        options={field.options.map((option) => ({ value: option, label: option }))}
                        value={(formField.value as string) ?? ''}
                        onValueChange={formField.onChange}
                        disabled={disabled}
                        placeholder="Select an option..."
                        searchPlaceholder="Search..."
                        aria-label={field.question}
                        asFormControl
                      />
                    )}

                    {displayType === 'image_select' && field.options && (
                      <FormControl>
                        <ImageSelectField
                          ref={formField.ref}
                          options={field.options}
                          optionImages={field.optionImages ?? {}}
                          value={(formField.value as string) ?? ''}
                          onChange={formField.onChange}
                          disabled={disabled}
                          fieldId={fieldId}
                        />
                      </FormControl>
                    )}

                    {/* Multi Select (Checkboxes) */}
                    {displayType === 'multi_select' && field.options && (
                      <div className="space-y-2">
                        {field.options.map((option, index) => {
                          const selectedValues = Array.isArray(formField.value)
                            ? (formField.value as string[])
                            : []
                          return (
                            <div key={option} className="flex items-center gap-2">
                              <Checkbox
                                ref={index === 0 ? formField.ref : undefined}
                                id={`${fieldId}-${option}`}
                                checked={selectedValues.includes(option)}
                                onCheckedChange={(checked) => {
                                  const newValues = checked
                                    ? [...selectedValues, option]
                                    : selectedValues.filter((v) => v !== option)
                                  formField.onChange(newValues)
                                }}
                                disabled={disabled}
                              />
                              <Label htmlFor={`${fieldId}-${option}`} className="font-normal cursor-pointer">
                                {option}
                              </Label>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Rank (drag-to-reorder) */}
                    {displayType === 'rank' && field.options && (
                      <RankField
                        ref={formField.ref}
                        value={
                          Array.isArray(formField.value) && formField.value.length > 0
                            ? (formField.value as string[])
                            : field.options
                        }
                        options={field.options}
                        onChange={formField.onChange}
                        disabled={disabled}
                        fieldId={fieldId}
                      />
                    )}

                    {/* Radio */}
                    {displayType === 'radio' && field.options && (
                      <RadioGroup
                        value={(formField.value as string) ?? ''}
                        onValueChange={formField.onChange}
                        disabled={disabled}
                      >
                        {field.options.map((option, index) => (
                          <div key={option} className="flex items-center gap-2">
                            <RadioGroupItem
                              ref={index === 0 ? formField.ref : undefined}
                              value={option}
                              id={`${fieldId}-${option}`}
                            />
                            <Label htmlFor={`${fieldId}-${option}`} className="font-normal cursor-pointer">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}

                    {/* Checkbox (single) */}
                    {displayType === 'checkbox' && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          ref={formField.ref}
                          id={fieldId}
                          checked={formField.value === 'true'}
                          onCheckedChange={(checked) => formField.onChange(checked ? 'true' : 'false')}
                          disabled={disabled}
                        />
                        <Label htmlFor={fieldId} className="font-normal cursor-pointer">
                          {field.question}
                        </Label>
                      </div>
                    )}

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            )
          })}
        </form>
      </Form>
    )
  },
)

interface ReadOnlyBriefProps {
  schema: BriefSchemaInput
  values: Record<string, string | string[]>
  /** 'default' uses label + plain text; 'embedded' uses gray rounded boxes */
  variant?: 'default' | 'embedded'
  /** 'conditional' respects showWhen; 'populated' shows any field that has stored data */
  visibilityMode?: 'conditional' | 'populated'
}

function getRenderableValue(value: string | string[] | undefined): string | string[] | null {
  if (Array.isArray(value)) {
    const items = value.map((item) => item.trim()).filter(Boolean)
    return items.length > 0 ? items : null
  }

  if (typeof value !== 'string') return null

  const trimmedValue = value.trim()
  return trimmedValue || null
}

export function ReadOnlyBrief({
  schema,
  values,
  variant = 'embedded',
  visibilityMode = 'conditional',
}: ReadOnlyBriefProps) {
  const normalizedSchema = useMemo(() => normalizeSchema(schema), [schema])
  const isDefault = variant === 'default'
  const renderableItems = useMemo(() => {
    type Item =
      | { kind: 'section'; field: SchemaField }
      | { kind: 'field'; field: SchemaField; value: string | string[] }

    // Map each schema field to a renderable item (or null if empty/hidden)
    const mapped = normalizedSchema
      .map((field): Item | null => {
        if (field.type === 'section') {
          if (visibilityMode === 'conditional' && !isFieldVisible(field.showWhen, values)) return null
          return { kind: 'section', field }
        }

        const value = getRenderableValue(values[String(field.id)])
        if (!value) return null
        if (visibilityMode === 'conditional' && !isFieldVisible(field.showWhen, values)) return null

        return { kind: 'field', field, value }
      })
      .filter((item): item is Item => item !== null)

    // Strip sections that have no fields after them
    return mapped.filter((item, i) => item.kind !== 'section' || mapped[i + 1]?.kind === 'field')
  }, [normalizedSchema, values, visibilityMode])

  return (
    <div className={isDefault ? 'space-y-6' : 'space-y-5'}>
      {renderableItems.map((item) => {
        if (item.kind === 'section') {
          const field = item.field

          return (
            <div key={`section-${field.id}`} className={isDefault ? 'pt-8 first:pt-0' : 'pt-6 first:pt-0'}>
              <h4
                className={
                  isDefault ? 'font-bold text-lg text-foreground' : 'font-bold text-base text-foreground'
                }
              >
                {field.title}
              </h4>
            </div>
          )
        }

        const { field, value } = item

        const fieldId = String(field.id)

        const isRank = field.field_type === 'rank' && Array.isArray(value)
        const isMultiList = field.displayAs === 'multi_list' && Array.isArray(value)
        const displayValue =
          isRank || isMultiList
            ? '' // handled separately below
            : Array.isArray(value)
              ? value.join(', ')
              : value
        const isLong = displayValue.length > 120

        if (isDefault) {
          return (
            <div key={fieldId}>
              <p className="font-semibold text-sm text-foreground">{field.question}</p>
              {isRank ? (
                <ol className="list-decimal list-inside mt-1.5 pl-3 text-sm font-medium leading-relaxed text-black space-y-0.5">
                  {(value as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ol>
              ) : isMultiList ? (
                <ul className="list-disc list-inside mt-1.5 pl-3 text-sm font-medium leading-relaxed text-black space-y-0.5">
                  {(value as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-black font-medium text-sm leading-relaxed mt-1.5 pl-3">{displayValue}</p>
              )}
            </div>
          )
        }

        return (
          <div key={fieldId} className="space-y-1.5">
            <p className="text-sm font-normal text-foreground">{field.question}</p>
            <div
              className={`bg-surface-soft rounded-lg px-3 py-2.5 text-sm text-foreground ${isLong ? 'min-h-[80px]' : ''}`}
            >
              {isRank ? (
                <ol className="list-decimal list-inside space-y-0.5">
                  {(value as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ol>
              ) : isMultiList ? (
                <ul className="list-disc list-inside space-y-0.5">
                  {(value as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                displayValue
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
